import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useTheme, useMediaQuery } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useFragment } from '@apollo/client/react';
import { gql } from '@apollo/client';
import {
  ContainerTypeLegacy,
  GrainBinDashboard,
  navigateTo,
  useAuth,
  useGetViewer,
  ContainerSelectionArgs,
} from '@amber-ui/core';
// Direct import: GrainBinPollingProvider is not exported from the barrel because
// it pulls in 5 polling hooks that would add to the barrel's initial transform graph.
// This is acceptable in a code-split leaf route (loaded lazily after server.warmup).
import { GrainBinPollingProvider } from '@amber-ui/core/src/component/grain-container/GrainBinPollingProvider';

/**
 * Colocated fragment for reading polling-related fields from the GrainBin cache entry.
 * The polling hooks write the full GrainBin entity to cache; this fragment subscribes
 * to the subset needed by GrainBinPollingProvider.
 *
 * Placed in the dashboard route (not the layout) so that polling starts when the
 * dashboard mounts and stops when the user navigates to Settings/Storage Periods/Tickets.
 * This is the 2026 best practice: colocate polling with the components that consume it.
 * Apollo automatically stops polling on unmount — no manual cleanup needed.
 */
const GRAIN_BIN_POLLING_DEPS_FRAGMENT = gql`
  fragment GrainBinDashboardPollingDeps on GrainBin {
    enable_fan_guidance
    fan_controllers {
      fan_controller_id_next
    }
  }
`;

type PollingDeps = {
  enable_fan_guidance?: boolean;
  fan_controllers?: { fan_controller_id_next: string }[];
};

function GrainBinDashboardRoute() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  // down('md') = < 960px, matches AuthLayout's mobile_width definition (width === 'sm' || 'xs').
  // Previously used down('sm') || only('xs') which was < 600px — inconsistent with AuthLayout.
  const matchesMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mobile_width = matchesMobile;

  // Read entity IDs from URL search params (source of truth)
  const { bin, acct } = Route.useSearch();
  const { account_id: session_account_id } = useAuth();
  const { viewer } = useGetViewer();

  const grain_bin_id = bin || null;
  const effectiveAccountId = acct || session_account_id || (viewer ? viewer.account_id : '');

  // Redirect to operations view when no grain bin is selected (matches old behavior)
  useEffect(() => {
    if (viewer && !grain_bin_id) {
      void navigate({ to: '/grain/operations', replace: true });
    }
  }, [viewer, grain_bin_id, navigate]);

  const goCreateBarge = useCallback(() => {
    navigateTo('/barge/create');
  }, []);
  const goCreatePile = useCallback(() => {
    navigateTo('/pile/create');
  }, []);
  const goCreateGrainBin = useCallback(() => {
    navigateTo('/grain_bin/create');
  }, []);
  const onSelectContainer = useCallback((selected: ContainerSelectionArgs) => {
    if (selected.container_type === ContainerTypeLegacy.bin) {
      navigateTo('/grain_bin/dashboard', selected);
    } else if (selected.container_type === ContainerTypeLegacy.barge) {
      navigateTo('/barge/dashboard', selected);
    } else if (selected.container_type === ContainerTypeLegacy.pile) {
      navigateTo('/pile/dashboard', selected);
    }
  }, []);

  if (!viewer) return <div>Loading viewer...</div>;
  if (!grain_bin_id) return null; // redirect is in flight

  return (
    <GrainBinPollingProviderWrapper grain_bin_id={grain_bin_id}>
      <GrainBinDashboard
        viewer={viewer}
        grain_bin_id={grain_bin_id}
        pathname={pathname}
        onSelectContainer={onSelectContainer}
        goCreateBarge={goCreateBarge}
        goCreatePile={goCreatePile}
        goCreateGrainBin={goCreateGrainBin}
        mobile_width={mobile_width}
        url_base="/grain_bin"
        current_account_id={effectiveAccountId}
      />
    </GrainBinPollingProviderWrapper>
  );
}

/**
 * Wrapper that reads polling dependencies from Apollo cache and renders
 * GrainBinPollingProvider. Colocated with the dashboard route so polling
 * starts/stops with the dashboard lifecycle — not the layout lifecycle.
 */
function GrainBinPollingProviderWrapper({
  grain_bin_id,
  children,
}: {
  grain_bin_id: string;
  children: React.ReactNode;
}) {
  const { data: grainBinPollingDeps, complete } = useFragment({
    fragment: GRAIN_BIN_POLLING_DEPS_FRAGMENT,
    from: { __typename: 'GrainBin', grain_bin_id },
  });

  const deps = grainBinPollingDeps as PollingDeps;

  // fan_controller_ids: New array reference on every re-render from .map().
  // This is acceptable because:
  // 1. useFragment only re-renders this component when enable_fan_guidance or
  //    fan_controllers actually changes in the cache (not on every poll).
  // 2. useFanControllerBackgroundPoll uses fan_controller_ids.length for skip
  //    and .map(createElement) — not as a useEffect dep. The React Compiler
  //    handles memoization of the createElement calls.
  const fan_controller_ids =
    complete && deps?.fan_controllers
      ? deps.fan_controllers.map((fc) => fc.fan_controller_id_next)
      : [];

  const enableFanGuidance = complete ? Boolean(deps?.enable_fan_guidance) : false;

  return (
    <GrainBinPollingProvider
      grain_bin_id={grain_bin_id}
      fan_controller_ids={fan_controller_ids}
      container_id={grain_bin_id}
      container_type={ContainerTypeLegacy.bin}
      enableFanGuidance={enableFanGuidance}
    >
      {children}
    </GrainBinPollingProvider>
  );
}

export const Route = createFileRoute('/_auth/_grain-bin/grain_bin/dashboard')({
  component: GrainBinDashboardRoute,
});
